import { MouseEventHandler, useState } from 'react';
import { UnsavedChangesDialog } from '@mews-commander/ui';
import { FloatingPanel } from '@mews/b2b-ui';
import { LocalizedText, useDialog } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import { Button, DialogFooterProps, LoadingOverlay, ProgressIndicator, Stack, StackItem } from '@optimus-web/core';
import { ConfigureSsoPanelConfig } from './ConfigureSsoPanelConfig/ConfigureSsoPanelConfig';
import { ConfigureSsoPanelDomains } from './ConfigureSsoPanelDomains/ConfigureSsoPanelDomains';
import { useConfigureSsoPanelForm } from './useConfigureSsoPanelForm';

enum ConfigureSsoPanelRoutes {
    ConnectionConfig = 'ConnectionConfig',
    DomainConfig = 'DomainConfig',
}

type ConfigureSsoPanelRouteContent = Record<
    ConfigureSsoPanelRoutes,
    { content: React.ReactNode; footer: DialogFooterProps }
>;

interface ConfigureSsoPanelProps {
    active: boolean;
    onClose: () => void;
}

const formId = 'configure-sso-panel-form';

export function ConfigureSsoPanel({ active, onClose }: ConfigureSsoPanelProps) {
    const [currentStep, setCurrentStep] = useState(ConfigureSsoPanelRoutes.ConnectionConfig);
    const [canNavigateDomains, setCanNavigateDomains] = useState(false);

    const [{ active: showUnsavedChangesDialog }, setUnsavedChangesDialogVisibility] = useDialog();

    const { renderFormContent, validateConfig, isSubmitting, hasDirtyFields, reset, hasValidConnectionConfig } =
        useConfigureSsoPanelForm({
            id: formId,
            onError: () => setCurrentStep(ConfigureSsoPanelRoutes.ConnectionConfig),
            onSuccess: onClose,
        });
    const handleClose = hasDirtyFields ? setUnsavedChangesDialogVisibility : onClose;
    const handleConfigSubmit: MouseEventHandler<HTMLButtonElement> = async (e) => {
        // For some reason, this button ignores the type="button" included and tries to submit the form
        // TODO: Investigate why this is happening
        e.preventDefault();

        const isValid = await validateConfig();
        if (!isValid) {
            return;
        }
        setCanNavigateDomains(true);
        setCurrentStep(ConfigureSsoPanelRoutes.DomainConfig);
    };

    const panelContent: ConfigureSsoPanelRouteContent = {
        [ConfigureSsoPanelRoutes.ConnectionConfig]: {
            content: <ConfigureSsoPanelConfig />,
            footer: {
                primary: (
                    <Button size="medium" type="button" onClick={handleConfigSubmit} variant="primary">
                        <LocalizedText textKey={TextKey.Next} />
                    </Button>
                ),
                secondary: (
                    <Button size="medium" variant="tertiary" type="button" onClick={handleClose}>
                        <LocalizedText textKey={TextKey.GoBack} />
                    </Button>
                ),
            },
        },
        [ConfigureSsoPanelRoutes.DomainConfig]: {
            content: <ConfigureSsoPanelDomains />,
            footer: {
                primary: (
                    <Button size="medium" type="submit" variant="primary" form={formId} loading={isSubmitting}>
                        <LocalizedText textKey={TextKey.Activate} />
                    </Button>
                ),
                secondary: (
                    <Button
                        size="medium"
                        variant="tertiary"
                        type="button"
                        onClick={() => setCurrentStep(ConfigureSsoPanelRoutes.ConnectionConfig)}
                    >
                        <LocalizedText textKey={TextKey.GoBack} />
                    </Button>
                ),
            },
        },
    };

    return (
        <>
            <FloatingPanel
                active={active}
                onClose={handleClose}
                modal
                title={<LocalizedText textKey={TextKey.SsoSetupTitle} />}
                footer={panelContent[currentStep].footer}
            >
                <Stack spacing="300" vertical itemAlignment="center">
                    <StackItem grow maxWidth="460px">
                        <ProgressIndicator
                            steps={[
                                {
                                    value: ConfigureSsoPanelRoutes.ConnectionConfig,
                                    label: <LocalizedText textKey={TextKey.SsoSetupOpenIdSectionTitle} />,
                                    editable: true,
                                },
                                {
                                    value: ConfigureSsoPanelRoutes.DomainConfig,
                                    label: <LocalizedText textKey={TextKey.UserDomains} />,
                                    editable: canNavigateDomains && hasValidConnectionConfig,
                                },
                            ]}
                            onStepChange={setCurrentStep}
                            activeStep={currentStep}
                        />
                    </StackItem>
                    <StackItem grow>
                        {isSubmitting && <LoadingOverlay show={isSubmitting} />}
                        {renderFormContent(panelContent[currentStep].content)}
                    </StackItem>
                </Stack>
            </FloatingPanel>

            <UnsavedChangesDialog
                active={showUnsavedChangesDialog}
                onCancel={setUnsavedChangesDialogVisibility}
                onDiscardChanges={() => {
                    setUnsavedChangesDialogVisibility();
                    onClose();
                    reset();
                }}
            />
        </>
    );
}
